import React from 'react';
import { motion } from 'framer-motion';
import { Check, Moon, PenTool, Clock } from 'lucide-react';
import { CTAButton } from './CTAButton';
export function ProductSection() {
  return <section className="py-24 px-6 bg-midnight relative overflow-hidden" data-id="element-178">
      {/* Background accent */}
      <div className="absolute top-1/4 right-0 w-[600px] h-[600px] bg-amber/5 rounded-full blur-[100px] pointer-events-none" data-id="element-179" />

      <div className="max-w-4xl mx-auto" data-id="element-180">
        {/* Reveal */}
        <div className="grid md:grid-cols-2 gap-12 items-center mb-24" data-id="element-181">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="order-2 md:order-1" data-id="element-182">
            <div className="relative" data-id="element-183">
              <div className="absolute inset-0 bg-amber-glow blur-2xl opacity-20" data-id="element-184" />
              <img src="https://cdn.magicpatterns.com/uploads/kxbhFqUatYvYNV8QCByADi/heylloyd-image-1770678748683.png" alt="Reset 3AM Protocol on tablet next to warm lamp" className="relative z-10 rounded-xl shadow-2xl border border-white/10 w-full" data-id="element-185" />
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="order-1 md:order-2 space-y-6" data-id="element-186">
            <span className="text-amber text-sm tracking-widest uppercase font-bold" data-id="element-187">
              Presento
            </span>
            <h2 className="text-4xl md:text-5xl font-heading text-cream leading-tight" data-id="element-188">
              RESET 3AM
              <span className="block text-2xl md:text-3xl text-warmgray mt-2 font-normal italic" data-id="element-189">
                Mini Protocolo de 14 Noches
              </span>
            </h2>
            <p className="text-lg text-cream-dim leading-relaxed" data-id="element-190">
              Un PDF práctico para mujeres 25+ que se despiertan 3–5am con la
              mente encendida.
            </p>
            <p className="text-lg text-cream-dim leading-relaxed" data-id="element-191">
              No promete magia. Promete algo mejor: <br data-id="element-192" />
              <span className="text-amber font-medium" data-id="element-193">
                un plan para que dejes de improvisar en el peor momento.
              </span>
            </p>
          </motion.div>
        </div>

        {/* What's Included */}
        <div className="space-y-12 mb-24" data-id="element-194">
          <h3 className="text-2xl font-heading text-center text-cream mb-10" data-id="element-195">
            ¿Qué incluye el protocolo?
          </h3>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-196">
            <FeatureCard icon={<Clock className="w-6 h-6 text-amber" data-id="element-198" />} title="Guión Apaga‑Bucle (90 segundos)" description="Una secuencia exacta (respiración + frases + enfoque sensorial) para cortar rumiación y bajar alerta." data-id="element-197" />
            <FeatureCard icon={<PenTool className="w-6 h-6 text-amber" data-id="element-200" />} title="Descarga de Preocupación (PM y 3AM)" description={'Papel + lápiz, pero con estructura: pendiente → próximo paso mínimo → hora. Tu mente suelta cuando sabe "cuándo".'} data-id="element-199" />
            <FeatureCard icon={<Moon className="w-6 h-6 text-amber" data-id="element-202" />} title="Rutina mínima de 7 minutos" description="Para reducir la probabilidad del despertar sin convertir tu noche en un proyecto." data-id="element-201" />
            <FeatureCard icon={<Check className="w-6 h-6 text-amber" data-id="element-204" />} title="Plan de 14 noches" description="Con un tracker simple (sin obsesión, sin perfeccionismo)." data-id="element-203" />
          </div>
        </div>

        {/* Why it works */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-darknavy p-8 md:p-12 rounded-2xl border border-white/5 text-center space-y-6" data-id="element-205">
          <h3 className="text-2xl md:text-3xl font-heading text-cream" data-id="element-206">
            Por qué esto funciona cuando lo demás falla
          </h3>
          <p className="text-lg text-cream-dim max-w-2xl mx-auto" data-id="element-207">
            Porque ataca el punto real del problema: <br data-id="element-208" />
            No es "que te despiertes". <br data-id="element-209" />
            Es que tu cerebro interpreta el despertar como una reunión urgente.
          </p>
          <p className="text-xl font-heading text-amber max-w-2xl mx-auto" data-id="element-210">
            Reset 3AM hace lo contrario: vuelve el momento aburrido, repetible y
            seguro.
          </p>
          <p className="text-lg text-cream-dim" data-id="element-211">
            Y cuando el cuerpo recibe esa señal… el sueño vuelve a tener
            espacio.
          </p>

          <div className="pt-8" data-id="element-212">
            <CTAButton data-id="element-213">Quiero el protocolo</CTAButton>
          </div>
        </motion.div>
      </div>
    </section>;
}
function FeatureCard({
  icon,
  title,
  description
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return <motion.div whileHover={{
    y: -5
  }} className="bg-white/5 p-6 rounded-xl border border-white/5 hover:border-amber/30 transition-colors" data-id="element-214">
      <div className="bg-midnight w-12 h-12 rounded-full flex items-center justify-center mb-4 border border-white/10" data-id="element-215">
        {icon}
      </div>
      <h4 className="text-xl font-heading text-cream mb-3" data-id="element-216">{title}</h4>
      <p className="text-warmgray leading-relaxed text-sm md:text-base" data-id="element-217">
        {description}
      </p>
    </motion.div>;
}